// slices/reportSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ReportService from "../services/ReportService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PROVIDER ROUTES --------

/**
 * Create a new report from a provider
 * POST /api/reports/create-report
 */
export const createProviderReport = createAsyncThunk(
  'report/createProviderReport',
  async (reportData, { rejectWithValue }) => {
    try {
      const response = await ReportService.createProviderReport(reportData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get provider's own reports
 * GET /api/reports/my-reports
 */
export const getProviderReports = createAsyncThunk(
  'report/getProviderReports',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ReportService.getProviderReports(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get report by ID for provider (only if they own it)
 * GET /api/reports/provider/:reportId
 */
export const getReportForProvider = createAsyncThunk(
  'report/getReportForProvider',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await ReportService.getReportForProvider(reportId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Provider replies to report communication
 * POST /api/reports/:reportId/reply
 */
export const providerReply = createAsyncThunk(
  'report/providerReply',
  async ({ reportId, message }, { rejectWithValue }) => {
    try {
      const response = await ReportService.providerReply(reportId, message);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Add feedback to resolved report
 * POST /api/reports/:reportId/feedback
 */
export const addFeedback = createAsyncThunk(
  'report/addFeedback',
  async ({ reportId, rating, comment }, { rejectWithValue }) => {
    try {
      const response = await ReportService.addFeedback(reportId, rating, comment);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ADMIN ROUTES --------

/**
 * Get all reports with filters (admin only)
 * GET /api/reports/
 */
export const getAllReports = createAsyncThunk(
  'report/getAllReports',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ReportService.getAllReports(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get report by ID (admin only)
 * GET /api/reports/:reportId
 */
export const getReportById = createAsyncThunk(
  'report/getReportById',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await ReportService.getReportById(reportId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Assign report to admin
 * PATCH /api/reports/:reportId/assign
 */
export const assignReportToAdmin = createAsyncThunk(
  'report/assignReportToAdmin',
  async ({ reportId, adminId = null }, { rejectWithValue }) => {
    try {
      const response = await ReportService.assignReportToAdmin(reportId, adminId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update report status
 * PATCH /api/reports/:reportId/status
 */
export const updateReportStatus = createAsyncThunk(
  'report/updateReportStatus',
  async ({ reportId, status }, { rejectWithValue }) => {
    try {
      const response = await ReportService.updateReportStatus(reportId, status);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Add admin response to report
 * POST /api/reports/:reportId/response
 */
export const addAdminResponse = createAsyncThunk(
  'report/addAdminResponse',
  async ({ reportId, message, actionTaken }, { rejectWithValue }) => {
    try {
      const response = await ReportService.addAdminResponse(reportId, message, actionTaken);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Add internal admin note
 * POST /api/reports/:reportId/notes
 */
export const addAdminNote = createAsyncThunk(
  'report/addAdminNote',
  async ({ reportId, note }, { rejectWithValue }) => {
    try {
      const response = await ReportService.addAdminNote(reportId, note);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Add communication with provider
 * POST /api/reports/:reportId/communicate
 */
export const communicateWithProvider = createAsyncThunk(
  'report/communicateWithProvider',
  async ({ reportId, message, isInternal = false }, { rejectWithValue }) => {
    try {
      const response = await ReportService.communicateWithProvider(reportId, message, isInternal);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Mark report as duplicate
 * PATCH /api/reports/:reportId/duplicate
 */
export const markAsDuplicate = createAsyncThunk(
  'report/markAsDuplicate',
  async ({ reportId, duplicateOfId }, { rejectWithValue }) => {
    try {
      const response = await ReportService.markAsDuplicate(reportId, duplicateOfId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Resolve report with action taken
 * PATCH /api/reports/:reportId/resolve
 */
export const resolveReport = createAsyncThunk(
  'report/resolveReport',
  async ({ reportId, actionTaken, description }, { rejectWithValue }) => {
    try {
      const response = await ReportService.resolveReport(reportId, actionTaken, description);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Dismiss report
 * PATCH /api/reports/:reportId/dismiss
 */
export const dismissReport = createAsyncThunk(
  'report/dismissReport',
  async ({ reportId, reason }, { rejectWithValue }) => {
    try {
      const response = await ReportService.dismissReport(reportId, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get reports by provider business name
 * GET /api/reports/business/:businessName
 */
export const getReportsByBusinessName = createAsyncThunk(
  'report/getReportsByBusinessName',
  async ({ businessName, params = {} }, { rejectWithValue }) => {
    try {
      const response = await ReportService.getReportsByBusinessName(businessName, params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- SUPER ADMIN ROUTES --------

/**
 * Escalate report to super admin
 * PATCH /api/reports/:reportId/escalate
 */
export const escalateReport = createAsyncThunk(
  'report/escalateReport',
  async ({ reportId, reason }, { rejectWithValue }) => {
    try {
      const response = await ReportService.escalateReport(reportId, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get report statistics
 * GET /api/reports/statistics
 */
export const getReportStatistics = createAsyncThunk(
  'report/getReportStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ReportService.getReportStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get detailed report analytics
 * GET /api/reports/analytics
 */
export const getReportAnalytics = createAsyncThunk(
  'report/getReportAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ReportService.getReportAnalytics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get unassigned reports count
 * GET /api/reports/unassigned/count
 */
export const getUnassignedCount = createAsyncThunk(
  'report/getUnassignedCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ReportService.getUnassignedCount();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get urgent reports count
 * GET /api/reports/urgent/count
 */
export const getUrgentCount = createAsyncThunk(
  'report/getUrgentCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ReportService.getUrgentCount();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Report data
  reports: [],
  currentReport: null,
  myReports: [],
  providerReport: null,
  reportsByBusiness: [],
  
  // Statistics
  statistics: null,
  analytics: null,
  unassignedCount: 0,
  urgentCount: 0,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  },
  
  // Filters
  filters: {},
  
  // Loading states
  loading: false,
  loadingMyReports: false,
  loadingReport: false,
  submitting: false,
  updating: false,
  loadingStatistics: false,
  
  // Error states
  error: null,
  submitError: null,
  updateError: null,
  
  // Success states
  submitSuccess: false,
  updateSuccess: false,
  replySuccess: false,
  feedbackSuccess: false,
  
  // UI states
  selectedReportId: null,
  showCommunication: false,
  showNotes: false,
  isEditing: false,
};

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    // Reset states
    resetReportState: (state) => {
      state.reports = [];
      state.currentReport = null;
      state.myReports = [];
      state.providerReport = null;
      state.reportsByBusiness = [];
      state.statistics = null;
      state.analytics = null;
      state.unassignedCount = 0;
      state.urgentCount = 0;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      state.filters = {};
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.replySuccess = false;
      state.feedbackSuccess = false;
      state.selectedReportId = null;
    },
    
    // Clear errors
    clearReportErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.updateError = null;
    },
    
    // Clear success states
    clearReportSuccess: (state) => {
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.replySuccess = false;
      state.feedbackSuccess = false;
    },
    
    // Set filters
    setReportFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearReportFilters: (state) => {
      state.filters = {};
    },
    
    // Set pagination
    setReportPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set selected report
    setSelectedReport: (state, action) => {
      state.selectedReportId = action.payload;
    },
    
    // Toggle communication
    toggleCommunication: (state) => {
      state.showCommunication = !state.showCommunication;
    },
    
    // Toggle notes
    toggleNotes: (state) => {
      state.showNotes = !state.showNotes;
    },
    
    // Set editing mode
    setEditingReport: (state, action) => {
      state.isEditing = action.payload;
    },
    
    // Update report locally (optimistic update)
    updateLocalReport: (state, action) => {
      const { reportId, updateData } = action.payload;
      
      // Update in reports list
      const reportIndex = state.reports.findIndex(r => r._id === reportId);
      if (reportIndex !== -1) {
        state.reports[reportIndex] = { ...state.reports[reportIndex], ...updateData };
      }
      
      // Update in my reports
      const myReportIndex = state.myReports.findIndex(r => r._id === reportId);
      if (myReportIndex !== -1) {
        state.myReports[myReportIndex] = { ...state.myReports[myReportIndex], ...updateData };
      }
      
      // Update current report
      if (state.currentReport && state.currentReport._id === reportId) {
        state.currentReport = { ...state.currentReport, ...updateData };
      }
    },
    
    // Add local report
    addLocalReport: (state, action) => {
      state.reports.unshift(action.payload);
      state.myReports.unshift(action.payload);
    },
    
    // Remove local report
    removeLocalReport: (state, action) => {
      const reportId = action.payload;
      state.reports = state.reports.filter(r => r._id !== reportId);
      state.myReports = state.myReports.filter(r => r._id !== reportId);
      if (state.currentReport && state.currentReport._id === reportId) {
        state.currentReport = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== CREATE PROVIDER REPORT ==========
      .addCase(createProviderReport.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(createProviderReport.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.myReports.unshift(reportData);
          state.reports.unshift(reportData);
        }
      })
      .addCase(createProviderReport.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to create report';
        state.submitSuccess = false;
      })
      
      // ========== GET PROVIDER REPORTS ==========
      .addCase(getProviderReports.pending, (state) => {
        state.loadingMyReports = true;
        state.error = null;
      })
      .addCase(getProviderReports.fulfilled, (state, action) => {
        state.loadingMyReports = false;
        const responseData = action.payload;
        
        // Handle different response structures
        if (responseData?.data?.data) {
          state.myReports = responseData.data.data || [];
          state.pagination = responseData.data.pagination || state.pagination;
        } else if (responseData?.data) {
          state.myReports = Array.isArray(responseData.data) ? responseData.data : [];
        } else if (Array.isArray(responseData)) {
          state.myReports = responseData;
        } else {
          state.myReports = [];
        }
        console.log('[ReportSlice] 📊 My reports loaded:', state.myReports.length);
      })
      .addCase(getProviderReports.rejected, (state, action) => {
        state.loadingMyReports = false;
        state.error = action.payload || 'Failed to get reports';
      })
      
      // ========== GET REPORT FOR PROVIDER ==========
      .addCase(getReportForProvider.pending, (state) => {
        state.loadingReport = true;
        state.error = null;
      })
      .addCase(getReportForProvider.fulfilled, (state, action) => {
        state.loadingReport = false;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.providerReport = reportData;
        state.currentReport = reportData;
      })
      .addCase(getReportForProvider.rejected, (state, action) => {
        state.loadingReport = false;
        state.error = action.payload || 'Failed to get report';
      })
      
      // ========== PROVIDER REPLY ==========
      .addCase(providerReply.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.replySuccess = false;
      })
      .addCase(providerReply.fulfilled, (state, action) => {
        state.submitting = false;
        state.replySuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        // Update in lists
        if (reportData) {
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(providerReply.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to send reply';
        state.replySuccess = false;
      })
      
      // ========== ADD FEEDBACK ==========
      .addCase(addFeedback.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.feedbackSuccess = false;
      })
      .addCase(addFeedback.fulfilled, (state, action) => {
        state.submitting = false;
        state.feedbackSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(addFeedback.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to add feedback';
        state.feedbackSuccess = false;
      })
      
      // ========== GET ALL REPORTS (ADMIN) ==========
      .addCase(getAllReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllReports.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        // Handle different response structures
        if (responseData?.data?.data) {
          // Structure: { success: true, data: { data: [...], pagination: {...} } }
          state.reports = responseData.data.data || [];
          state.pagination = responseData.data.pagination || state.pagination;
        } else if (responseData?.data) {
          // Structure: { success: true, data: [...] }
          state.reports = Array.isArray(responseData.data) ? responseData.data : [];
        } else if (Array.isArray(responseData)) {
          // Structure: [...]
          state.reports = responseData;
        } else {
          state.reports = [];
        }
        
        console.log('[ReportSlice] 📊 Reports loaded:', state.reports.length);
      })
      .addCase(getAllReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get reports';
      })
      
      // ========== GET REPORT BY ID (ADMIN) ==========
      .addCase(getReportById.pending, (state) => {
        state.loadingReport = true;
        state.error = null;
      })
      .addCase(getReportById.fulfilled, (state, action) => {
        state.loadingReport = false;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
      })
      .addCase(getReportById.rejected, (state, action) => {
        state.loadingReport = false;
        state.error = action.payload || 'Failed to get report';
      })
      
      // ========== ASSIGN REPORT TO ADMIN ==========
      .addCase(assignReportToAdmin.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(assignReportToAdmin.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(assignReportToAdmin.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to assign report';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE REPORT STATUS ==========
      .addCase(updateReportStatus.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateReportStatus.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(updateReportStatus.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update status';
        state.updateSuccess = false;
      })
      
      // ========== ADD ADMIN RESPONSE ==========
      .addCase(addAdminResponse.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(addAdminResponse.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(addAdminResponse.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to add response';
        state.updateSuccess = false;
      })
      
      // ========== ADD ADMIN NOTE ==========
      .addCase(addAdminNote.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(addAdminNote.fulfilled, (state, action) => {
        state.updating = false;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(addAdminNote.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to add note';
      })
      
      // ========== COMMUNICATE WITH PROVIDER ==========
      .addCase(communicateWithProvider.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(communicateWithProvider.fulfilled, (state, action) => {
        state.updating = false;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(communicateWithProvider.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to send communication';
      })
      
      // ========== MARK AS DUPLICATE ==========
      .addCase(markAsDuplicate.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(markAsDuplicate.fulfilled, (state, action) => {
        state.updating = false;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(markAsDuplicate.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to mark as duplicate';
      })
      
      // ========== RESOLVE REPORT ==========
      .addCase(resolveReport.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(resolveReport.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(resolveReport.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to resolve report';
        state.updateSuccess = false;
      })
      
      // ========== DISMISS REPORT ==========
      .addCase(dismissReport.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(dismissReport.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(dismissReport.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to dismiss report';
        state.updateSuccess = false;
      })
      
      // ========== GET REPORTS BY BUSINESS NAME ==========
      .addCase(getReportsByBusinessName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportsByBusinessName.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        if (responseData?.data?.data) {
          state.reportsByBusiness = responseData.data.data || [];
          state.pagination = responseData.data.pagination || state.pagination;
        } else if (responseData?.data) {
          state.reportsByBusiness = Array.isArray(responseData.data) ? responseData.data : [];
        } else if (Array.isArray(responseData)) {
          state.reportsByBusiness = responseData;
        } else {
          state.reportsByBusiness = [];
        }
      })
      .addCase(getReportsByBusinessName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get reports by business';
      })
      
      // ========== ESCALATE REPORT ==========
      .addCase(escalateReport.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(escalateReport.fulfilled, (state, action) => {
        state.updating = false;
        const responseData = action.payload;
        const reportData = responseData?.data || responseData;
        state.currentReport = reportData;
        if (reportData) {
          state.reports = state.reports.map(r => 
            r._id === reportData._id ? reportData : r
          );
          state.myReports = state.myReports.map(r => 
            r._id === reportData._id ? reportData : r
          );
        }
      })
      .addCase(escalateReport.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to escalate report';
      })
      
      // ========== GET REPORT STATISTICS ==========
      .addCase(getReportStatistics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getReportStatistics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        const responseData = action.payload;
        
        // Handle different response structures
        if (responseData?.data?.data) {
          state.statistics = responseData.data.data;
        } else if (responseData?.data) {
          state.statistics = responseData.data;
        } else {
          state.statistics = responseData;
        }
        
        console.log('📊 SAVED REPORT STATISTICS:', state.statistics);
      })
      .addCase(getReportStatistics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get statistics';
      })
      
      // ========== GET REPORT ANALYTICS ==========
      .addCase(getReportAnalytics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getReportAnalytics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        const responseData = action.payload;
        state.analytics = responseData?.data || responseData;
      })
      .addCase(getReportAnalytics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get analytics';
      })
      
      // ========== GET UNASSIGNED COUNT ==========
      .addCase(getUnassignedCount.fulfilled, (state, action) => {
        const responseData = action.payload;
        state.unassignedCount = responseData?.data?.count || responseData?.count || 0;
      })
      
      // ========== GET URGENT COUNT ==========
      .addCase(getUrgentCount.fulfilled, (state, action) => {
        const responseData = action.payload;
        state.urgentCount = responseData?.data?.count || responseData?.count || 0;
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectReportState = (state) => state.report;

// Report data selectors
export const selectReports = createSelector(
  selectReportState,
  (report) => report.reports
);

export const selectCurrentReport = createSelector(
  selectReportState,
  (report) => report.currentReport
);

export const selectMyReports = createSelector(
  selectReportState,
  (report) => report.myReports
);

export const selectProviderReport = createSelector(
  selectReportState,
  (report) => report.providerReport
);

export const selectReportsByBusiness = createSelector(
  selectReportState,
  (report) => report.reportsByBusiness
);

// Statistics selectors
export const selectReportStatistics = createSelector(
  selectReportState,
  (report) => report.statistics
);

export const selectReportAnalytics = createSelector(
  selectReportState,
  (report) => report.analytics
);

export const selectUnassignedCount = createSelector(
  selectReportState,
  (report) => report.unassignedCount
);

export const selectUrgentCount = createSelector(
  selectReportState,
  (report) => report.urgentCount
);

// Pagination selectors
export const selectReportPagination = createSelector(
  selectReportState,
  (report) => report.pagination
);

// Loading selectors
export const selectReportsLoading = createSelector(
  selectReportState,
  (report) => report.loading
);

export const selectMyReportsLoading = createSelector(
  selectReportState,
  (report) => report.loadingMyReports
);

export const selectReportLoading = createSelector(
  selectReportState,
  (report) => report.loadingReport
);

export const selectReportSubmitting = createSelector(
  selectReportState,
  (report) => report.submitting
);

export const selectReportUpdating = createSelector(
  selectReportState,
  (report) => report.updating
);

export const selectStatisticsLoading = createSelector(
  selectReportState,
  (report) => report.loadingStatistics
);

// Error selectors
export const selectReportError = createSelector(
  selectReportState,
  (report) => report.error
);

export const selectSubmitReportError = createSelector(
  selectReportState,
  (report) => report.submitError
);

export const selectUpdateReportError = createSelector(
  selectReportState,
  (report) => report.updateError
);

// Success selectors
export const selectSubmitReportSuccess = createSelector(
  selectReportState,
  (report) => report.submitSuccess
);

export const selectUpdateReportSuccess = createSelector(
  selectReportState,
  (report) => report.updateSuccess
);

export const selectReplySuccess = createSelector(
  selectReportState,
  (report) => report.replySuccess
);

export const selectFeedbackSuccess = createSelector(
  selectReportState,
  (report) => report.feedbackSuccess
);

// UI selectors
export const selectSelectedReportId = createSelector(
  selectReportState,
  (report) => report.selectedReportId
);

export const selectShowCommunication = createSelector(
  selectReportState,
  (report) => report.showCommunication
);

export const selectShowNotes = createSelector(
  selectReportState,
  (report) => report.showNotes
);

export const selectIsEditingReport = createSelector(
  selectReportState,
  (report) => report.isEditing
);

// Derived selectors
export const selectReportsByStatus = createSelector(
  selectReports,
  (reports) => {
    if (!reports || !Array.isArray(reports)) return {};
    return reports.reduce((acc, report) => {
      const status = report?.status || 'pending';
      if (!acc[status]) acc[status] = [];
      acc[status].push(report);
      return acc;
    }, {});
  }
);

export const selectReportsBySeverity = createSelector(
  selectReports,
  (reports) => {
    if (!reports || !Array.isArray(reports)) return {};
    return reports.reduce((acc, report) => {
      const severity = report?.severity || 'medium';
      if (!acc[severity]) acc[severity] = [];
      acc[severity].push(report);
      return acc;
    }, {});
  }
);

export const selectPendingReports = createSelector(
  selectReports,
  (reports) => reports?.filter(r => r?.status === 'pending') || []
);

export const selectUnderReviewReports = createSelector(
  selectReports,
  (reports) => reports?.filter(r => r?.status === 'under_review') || []
);

export const selectResolvedReports = createSelector(
  selectReports,
  (reports) => reports?.filter(r => r?.status === 'resolved') || []
);

export const selectUrgentReports = createSelector(
  selectReports,
  (reports) => reports?.filter(r => r?.severity === 'critical' || r?.priority === 'urgent') || []
);

export const selectMyPendingReports = createSelector(
  selectMyReports,
  (reports) => reports?.filter(r => r?.status === 'pending') || []
);

// ==================== EXPORTS ====================

export const {
  resetReportState,
  clearReportErrors,
  clearReportSuccess,
  setReportFilters,
  clearReportFilters,
  setReportPagination,
  setSelectedReport,
  toggleCommunication,
  toggleNotes,
  setEditingReport,
  updateLocalReport,
  addLocalReport,
  removeLocalReport,
} = reportSlice.actions;

export default reportSlice.reducer;